/**
 * collectSceneHierarchies — INIT-2026-Q3-SCENE-HIERARCHY multi-layer viewer (V1).
 *
 * Scans all model-bearing scene-item slots and returns one
 * LayerHierarchyRef per scene item that carries a `hierarchyUrl`.
 * This replaces the single-result `findSceneHierarchyUrl` for the
 * multi-theme CityGML delivery path (N layers, each with its own
 * object-index hierarchy).
 */

/** Describes one layer's hierarchy reference from the gTwin payload. */
export interface LayerHierarchyRef {
  layerId: string;
  displayName: string;
  hierarchyUrl: string;
  blockTiled: boolean;
  // INIT-2026-Q3-SCENE-HIERARCHY V-CRS-B — coordinate-system passthrough.
  sourceCrs?: string;
  displayCrs?: string;
  verticalDatum?: string;
  crsProvenanceUrl?: string;
}

/** Scene slots that can carry a model hierarchy (basemaps/terrain never do). */
const MODEL_SLOTS = [
  "realityData",
  "engineeringModels",
  "featureLayers",
  "overlays",
] as const;

/**
 * Return all `LayerHierarchyRef`s found across the model slots.
 *
 * @param geo - The `payload.geospatial` object (typed loosely; shape-tolerant).
 */
export function collectSceneHierarchies(geo: unknown): LayerHierarchyRef[] {
  const scene = (geo as { scene?: Record<string, unknown> } | undefined)?.scene;
  if (!scene || typeof scene !== "object") return [];

  const out: LayerHierarchyRef[] = [];
  for (const slot of MODEL_SLOTS) {
    const items = scene[slot];
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      const url = (item as { hierarchyUrl?: unknown } | null)?.hierarchyUrl;
      if (typeof url === "string" && url.length > 0) {
        const it = item as {
          sourceCrs?: unknown;
          displayCrs?: unknown;
          verticalDatum?: unknown;
          crsProvenanceUrl?: unknown;
        };
        out.push({
          layerId: (item as { id?: string }).id ?? `layer-${out.length}`,
          displayName:
            (item as { displayName?: string }).displayName ??
            (item as { id?: string }).id ??
            `Layer ${out.length}`,
          hierarchyUrl: url,
          blockTiled: !!(item as { blockTiled?: boolean }).blockTiled,
          sourceCrs: typeof it.sourceCrs === "string" ? it.sourceCrs : undefined,
          displayCrs: typeof it.displayCrs === "string" ? it.displayCrs : undefined,
          verticalDatum: typeof it.verticalDatum === "string" ? it.verticalDatum : undefined,
          crsProvenanceUrl:
            typeof it.crsProvenanceUrl === "string" ? it.crsProvenanceUrl : undefined,
        });
      }
    }
  }
  return out;
}
